import { Box, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { trackClick } from "@/components/_helpers";
import { cardSlides } from "@/components/merch/cardSlides";
import { BODY, EASE, GOLD, HAIRLINE_COLOR, HEADING, ON_GOLD, SURFACE } from "@/components/merch/merchTheme";
import type { LifestyleImage } from "@/components/merch/useLifestyleImages";
import { useSnapStrip } from "@/components/merch/useSnapStrip";
import { MOCKUP_HEIGHT, MOCKUP_WIDTH } from "@/utils/merchMockups";
import { CARD_WIDTHS, merchImageSrcSet, merchImageUrl } from "@/utils/merchImages";
import { formatMoney } from "@/utils/merchPricing";
import { colorCountLabel, itemName, itemPath, type StoreListItem } from "@/utils/merchStore";

const CYCLE_MS = 1100;

const CARD_SIZES = "(max-width: 600px) 46vw, (max-width: 1024px) 30vw, 280px";

const MAX_SWATCHES = 5;

export const CARD_NAME_SIZE = { xs: "17px", md: "16px" };

export const CARD_TEXT = {
  nameTop: { xs: "10px", md: "12px" },
  nameLineHeight: 1.15,
  nameLines: { xs: 3, md: 2 },
  priceTop: "6px",
  priceHeight: { xs: "18px", md: "17px" },
  detailTop: "5px",
  detailSize: { xs: "13px", md: "12px" },
  detailLineHeight: 1.45,
  detailLines: 2,
};

const reservedLines = (size: string, lineHeight: number, lines: number) => `calc(${size} * ${lineHeight} * ${lines})`;

export const cardNameHeight = (size: { xs: string; md: string } = CARD_NAME_SIZE) => ({
  xs: reservedLines(size.xs, CARD_TEXT.nameLineHeight, CARD_TEXT.nameLines.xs),
  md: reservedLines(size.md, CARD_TEXT.nameLineHeight, CARD_TEXT.nameLines.md),
});

export const cardDetailHeight = {
  xs: reservedLines(CARD_TEXT.detailSize.xs, CARD_TEXT.detailLineHeight, CARD_TEXT.detailLines),
  md: reservedLines(CARD_TEXT.detailSize.md, CARD_TEXT.detailLineHeight, CARD_TEXT.detailLines),
};

const clampLines = (lines: number | { xs: number; md: number }) => ({
  display: "-webkit-box",
  WebkitBoxOrient: "vertical" as const,
  WebkitLineClamp: lines,
  overflow: "hidden",
});

export interface ProductCardProps {
  item: StoreListItem;
  alsoAs: string | null;
  lifestyle?: LifestyleImage[];
  priority?: boolean;
  clickTarget: string;
  onVisible?: (item: StoreListItem) => void;
  nameSize?: { xs: string; md: string };
  showRelease?: boolean;
}

export function ProductCard({
  item,
  alsoAs,
  lifestyle = [],
  priority = false,
  clickTarget,
  onVisible,
  nameSize = CARD_NAME_SIZE,
  showRelease = false,
}: ProductCardProps) {
  const slides = useMemo(() => cardSlides(item, lifestyle), [item, lifestyle]);
  const name = itemName(item);
  const resetKey = `${item.designKey}:${item.sku}`;
  const { stripRef, slideRef, active, goTo, handlers } = useSnapStrip(slides.length, resetKey);
  const [activated, setActivated] = useState(false);
  const timer = useRef<number | null>(null);
  const activeRef = useRef(active);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const seen = useRef(false);
  activeRef.current = active;

  const stopCycle = useCallback(() => {
    if (timer.current !== null) {
      window.clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => stopCycle, [stopCycle]);

  useEffect(() => {
    setActivated(false);
    stopCycle();
  }, [resetKey, stopCycle]);

  useEffect(() => {
    if (!onVisible || seen.current || typeof IntersectionObserver === "undefined") return;
    const node = rootRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting) || seen.current) return;
        seen.current = true;
        observer.disconnect();
        onVisible(item);
      },
      { rootMargin: "120px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [item, onVisible]);

  const startCycle = useCallback(
    (event: React.PointerEvent) => {
      if (slides.length < 2 || event.pointerType !== "mouse") return;
      setActivated(true);
      stopCycle();
      goTo(1);
      timer.current = window.setInterval(() => goTo((activeRef.current + 1) % slides.length), CYCLE_MS);
    },
    [goTo, slides.length, stopCycle],
  );

  const endCycle = useCallback(
    (event: React.PointerEvent) => {
      if (event.pointerType !== "mouse") return;
      stopCycle();
      goTo(0);
    },
    [goTo, stopCycle],
  );

  const advance = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setActivated(true);
      goTo((activeRef.current + 1) % Math.max(slides.length, 1));
    },
    [goTo, slides.length],
  );

  const onStripTouchStart = useCallback(() => {
    setActivated(true);
    handlers.onTouchStart();
  }, [handlers]);

  const chosenColor = item.colors.find((color) => color.name === item.defaultColor);
  const swatches = item.colors.slice(0, MAX_SWATCHES);
  const moreSwatches = item.colors.length - swatches.length;

  return (
    <Box ref={rootRef} sx={{ position: "relative", minWidth: 0 }}>
      <Box
        component={RouterLink}
        to={itemPath(item, item.defaultColor)}
        onClick={() => trackClick(`${clickTarget}:${item.designSlug}:${item.productSlug}`, { label: name, section: "merch" })}
        onPointerEnter={startCycle}
        onPointerLeave={endCycle}
        onFocus={() => setActivated(true)}
        aria-label={`${name}, ${formatMoney(item.priceCents, item.currency)}, ${colorCountLabel(item.colors.length)}`}
        sx={{
          display: "block",
          minWidth: 0,
          textDecoration: "none",
          borderRadius: "12px",
          "&:focus-visible": { outline: `2px solid ${GOLD}`, outlineOffset: "6px" },
          "&:hover .merch-card__art": { transform: "scale(1.02)" },
          "@media (hover: hover)": { "&:hover .merch-card__name": { color: BODY } },
          "&:active .merch-card__art": { transform: "scale(0.995)" },
          "@media (prefers-reduced-motion: reduce)": {
            "&:hover .merch-card__art": { transform: "none" },
            "&:active .merch-card__art": { transform: "none" },
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: "12px",
            backgroundColor: SURFACE,
            aspectRatio: `${MOCKUP_WIDTH} / ${MOCKUP_HEIGHT}`,
          }}
        >
          <Box className="merch-card__art" sx={{ position: "absolute", inset: 0, transition: `transform 480ms ${EASE}` }}>
            {slides.length === 0 ? (
              <Box role="img" aria-label={name} sx={{ position: "absolute", inset: 0, backgroundColor: chosenColor?.hex }} />
            ) : (
              <Box
                ref={stripRef}
                data-card-strip=""
                onScroll={handlers.onScroll}
                onTouchStart={onStripTouchStart}
                onTouchEnd={handlers.onTouchEnd}
                onTouchCancel={handlers.onTouchCancel}
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  overflowX: slides.length > 1 ? "auto" : "hidden",
                  overscrollBehaviorX: "contain",
                  scrollSnapType: "x mandatory",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                }}
              >
                {slides.map((slide, slideIndex) => (
                  <Box
                    key={slide.key}
                    ref={slideRef(slideIndex)}
                    data-card-slide=""
                    sx={{
                      position: "relative",
                      flex: "0 0 100%",
                      height: "100%",
                      scrollSnapAlign: "start",
                      scrollSnapStop: "always",
                      backgroundColor: slide.kind === "print" ? chosenColor?.hex : undefined,
                    }}
                  >
                    {slideIndex === 0 || activated ? (
                      <Box
                        component="img"
                        src={merchImageUrl(slide.url, CARD_WIDTHS[CARD_WIDTHS.length - 1]) ?? slide.url}
                        srcSet={merchImageSrcSet(slide.url, CARD_WIDTHS)}
                        sizes={CARD_SIZES}
                        alt={slideIndex === 0 ? slide.alt : ""}
                        aria-hidden={slideIndex === 0 ? undefined : true}
                        draggable={false}
                        loading={priority && slideIndex === 0 ? "eager" : "lazy"}
                        decoding="async"
                        sx={{
                          position: "absolute",
                          inset: slide.kind === "print" ? "16%" : 0,
                          width: slide.kind === "print" ? "68%" : "100%",
                          height: slide.kind === "print" ? "68%" : "100%",
                          objectFit: slide.kind === "print" ? "contain" : "cover",
                          display: "block",
                        }}
                      />
                    ) : null}
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {slides.length > 1 ? (
            <>
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: "10px",
                  display: "none",
                  justifyContent: "center",
                  gap: "5px",
                  pointerEvents: "none",
                  "@media (hover: hover) and (pointer: fine)": { display: "flex" },
                }}
              >
                {slides.map((slide, slideIndex) => (
                  <Box
                    key={slide.key}
                    data-card-dot=""
                    data-active={slideIndex === active ? "true" : "false"}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: slideIndex === active ? GOLD : "rgba(255, 255, 255, 0.55)",
                      boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.25)",
                      transition: `background-color 160ms ${EASE}`,
                    }}
                  />
                ))}
              </Box>
            </>
          ) : null}

          {item.featured && !item.wordmark ? (
            <Box
              component="span"
              sx={{
                position: "absolute",
                top: "10px",
                left: "10px",
                px: "9px",
                py: "4px",
                borderRadius: "999px",
                backgroundColor: GOLD,
                color: ON_GOLD,
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "11px", md: "10px" },
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                pointerEvents: "none",
              }}
            >
              Featured
            </Box>
          ) : null}
        </Box>

        <Typography
          component="h3"
          className="merch-card__name"
          sx={{
            m: 0,
            mt: CARD_TEXT.nameTop,
            fontFamily: "Hanson, sans-serif",
            fontWeight: 700,
            fontSize: nameSize,
            lineHeight: CARD_TEXT.nameLineHeight,
            color: HEADING,
            minWidth: 0,
            minHeight: cardNameHeight(nameSize),
            overflowWrap: "break-word",
            ...clampLines(CARD_TEXT.nameLines),
          }}
        >
          {name}
        </Typography>

        <Box sx={{ mt: CARD_TEXT.priceTop, height: CARD_TEXT.priceHeight, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", minWidth: 0 }}>
          <Typography
            component="span"
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: { xs: "15px", md: "14px" },
              fontWeight: 600,
              color: GOLD,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            {formatMoney(item.priceCents, item.currency)}
          </Typography>
          <Box role="img" aria-label={colorCountLabel(item.colors.length)} sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
            {swatches.map((color) => (
              <Box
                key={color.name}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: color.hex,
                  boxShadow: `inset 0 0 0 1px ${HAIRLINE_COLOR}`,
                }}
              />
            ))}
            {moreSwatches > 0 ? (
              <Box
                component="span"
                aria-hidden
                sx={{ ml: "2px", fontFamily: "Poppins, sans-serif", fontSize: "12px", fontWeight: 600, lineHeight: 1, color: BODY, opacity: 0.75, fontVariantNumeric: "tabular-nums" }}
              >
                +{moreSwatches}
              </Box>
            ) : null}
          </Box>
        </Box>

        <Typography
          component="p"
          sx={{
            mt: CARD_TEXT.detailTop,
            mb: 0,
            minHeight: cardDetailHeight,
            fontFamily: "Poppins, sans-serif",
            fontSize: CARD_TEXT.detailSize,
            lineHeight: CARD_TEXT.detailLineHeight,
            color: BODY,
            opacity: 0.62,
            ...clampLines(CARD_TEXT.detailLines),
          }}
        >
          {item.productName}
          {alsoAs ? `. ${alsoAs}` : ""}
        </Typography>
      </Box>

      {slides.length > 1 ? (
        <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, aspectRatio: `${MOCKUP_WIDTH} / ${MOCKUP_HEIGHT}`, pointerEvents: "none" }}>
          <Box
            component="button"
            type="button"
            onClick={advance}
            aria-label={`Show the next photo of ${name}`}
            sx={{
              position: "absolute",
              right: "8px",
              bottom: "8px",
              minWidth: 44,
              minHeight: 44,
              px: "12px",
              display: { xs: "inline-flex", md: "none" },
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "999px",
              border: "1px solid rgba(255, 255, 255, 0.25)",
              backgroundColor: "rgba(10, 10, 12, 0.66)",
              color: "#f4efe6",
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 600,
              letterSpacing: "0.04em",
              cursor: "pointer",
              pointerEvents: "auto",
              backdropFilter: "blur(8px)",
              "&:active": { transform: "scale(0.96)" },
              "&:focus-visible": { outline: `2px solid ${GOLD}`, outlineOffset: "2px" },
              "@media (hover: hover) and (pointer: fine)": { display: "none" },
            }}
          >
            {active + 1}/{slides.length}
          </Box>
        </Box>
      ) : null}

      {showRelease && item.releasePath && item.releaseName ? (
        <Typography
          component={RouterLink}
          to={item.releasePath}
          onClick={() => {
            trackClick(`merch:card-release:${item.designSlug}`, { label: item.releaseName ?? "", section: "merch" });
          }}
          sx={{
            mt: { xs: "-8px", md: "4px" },
            mb: { xs: "-12px", md: 0 },
            py: { xs: "12px", md: 0 },
            display: "inline-block",
            fontFamily: "Poppins, sans-serif",
            fontSize: { xs: "13px", md: "12px" },
            lineHeight: 1.45,
            color: GOLD,
            textDecoration: "none",
            borderBottom: "1px solid transparent",
            "&:hover": { borderBottomColor: "currentColor" },
            "&:focus-visible": { outline: `2px solid ${GOLD}`, outlineOffset: "2px", borderRadius: "2px" },
          }}
        >
          Hear {item.releaseName}
        </Typography>
      ) : null}
    </Box>
  );
}

export default ProductCard;
